// ===========================================
// BUSTED! BLACKJACK - TABLE LAYOUT STORE
// ===========================================
// Zustand store for managing table layout and animation endpoints.
// Provides the single source of truth for all position-based animations.
//
// ARCHITECTURE:
// - Server sends layout when player joins table
// - DEV CONTROLS can modify layout (broadcasts to all clients)
// - Animation code queries endpoints by ID
// - Works both inside React components and outside (animation utils)
// ===========================================

import { create } from 'zustand';
import {
  TableLayout,
  AnimationEndpoint,
  Point2D,
  LayoutQueries,
  BlackjackEndpointIds,
  createDefaultBlackjackLayout,
  updateEndpointPosition,
  updateEndpointScale,
  updateEndpoints,
  BLACKJACK_DENOMINATIONS,
} from '@busted-blackjack/shared';

// ===========================================
// TYPES
// ===========================================

interface LayoutState {
  // Current table layout (null if not yet loaded)
  layout: TableLayout | null;

  // Layout version for cache invalidation
  version: number;

  // Whether layout is being modified by DEV CONTROLS
  isEditing: boolean;

  // Actions
  setLayout: (layout: TableLayout) => void;
  clearLayout: () => void;

  // DEV CONTROLS mutations (local + will broadcast to server)
  updatePosition: (endpointId: string, position: Partial<Point2D>) => void;
  updateScale: (endpointId: string, scale: number) => void;
  batchUpdate: (updates: Array<{ id: string; position?: Partial<Point2D>; scale?: number; rotation?: number }>) => void;
  setEditing: (editing: boolean) => void;

  // Initialize with default layout (for development/testing)
  initializeDefault: () => void;

  // Query helpers (convenience methods that use LayoutQueries internally)
  getEndpoint: (id: string) => AnimationEndpoint | undefined;
  getGlobalPosition: (id: string) => Point2D | undefined;

  // Blackjack-specific queries
  getKittyPosition: (visualSeatPos: number) => Point2D | undefined;
  getTrayChipPosition: (denomination: number) => Point2D | undefined;
  getRailChipPosition: (denomination: number) => Point2D | undefined;
  getStagedBetPosition: () => Point2D | undefined;
  getShoePosition: () => Point2D | undefined;
  getDealerPosition: () => Point2D | undefined;
}

// ===========================================
// STORE
// ===========================================

export const useLayoutStore = create<LayoutState>((set, get) => ({
  // Initial state
  layout: null,
  version: 0,
  isEditing: false,

  // ===========================================
  // CORE ACTIONS
  // ===========================================

  setLayout: (layout: TableLayout) => {
    console.log('[LayoutStore] Layout set, version:', layout.version, 'endpoints:', Object.keys(layout.endpoints).length);
    set({ layout, version: layout.version });
  },

  clearLayout: () => {
    console.log('[LayoutStore] Layout cleared');
    set({ layout: null, version: 0 });
  },

  // ===========================================
  // DEV CONTROLS MUTATIONS
  // ===========================================

  updatePosition: (endpointId: string, position: Partial<Point2D>) => {
    const { layout } = get();
    if (!layout) return;

    const newLayout = updateEndpointPosition(layout, endpointId, position);
    console.log('[LayoutStore] Position updated:', endpointId, position);
    set({ layout: newLayout, version: newLayout.version });
  },

  updateScale: (endpointId: string, scale: number) => {
    const { layout } = get();
    if (!layout) return;

    const newLayout = updateEndpointScale(layout, endpointId, scale);
    console.log('[LayoutStore] Scale updated:', endpointId, scale);
    set({ layout: newLayout, version: newLayout.version });
  },

  batchUpdate: (updates) => {
    const { layout } = get();
    if (!layout) return;

    const newLayout = updateEndpoints(layout, updates);
    console.log('[LayoutStore] Batch update:', updates.length, 'endpoints');
    set({ layout: newLayout, version: newLayout.version });
  },

  setEditing: (editing: boolean) => {
    set({ isEditing: editing });
  },

  // ===========================================
  // INITIALIZATION
  // ===========================================

  initializeDefault: () => {
    const defaultLayout = createDefaultBlackjackLayout();
    console.log('[LayoutStore] Initialized with default Blackjack layout');
    set({ layout: defaultLayout, version: defaultLayout.version });
  },

  // ===========================================
  // QUERY HELPERS
  // ===========================================

  getEndpoint: (id: string) => {
    const { layout } = get();
    if (!layout) return undefined;
    return LayoutQueries.getEndpoint(layout, id);
  },

  getGlobalPosition: (id: string) => {
    const { layout } = get();
    if (!layout) return undefined;
    return LayoutQueries.getGlobalPosition(layout, id);
  },

  // ===========================================
  // BLACKJACK-SPECIFIC QUERIES
  // ===========================================

  getKittyPosition: (visualSeatPos: number) => {
    const { layout } = get();
    if (!layout) return undefined;
    const endpoint = LayoutQueries.getKitty(layout, visualSeatPos);
    return endpoint?.position;
  },

  getTrayChipPosition: (denomination: number) => {
    const { layout } = get();
    if (!layout) return undefined;
    const endpoint = LayoutQueries.getTrayChip(layout, denomination);
    return endpoint?.position;
  },

  getRailChipPosition: (denomination: number) => {
    const { layout } = get();
    if (!layout) return undefined;
    const endpoint = LayoutQueries.getRailChip(layout, denomination);
    return endpoint?.position;
  },

  getStagedBetPosition: () => {
    const { layout } = get();
    if (!layout) return undefined;
    const endpoint = LayoutQueries.getStagedBet(layout);
    return endpoint?.position;
  },

  getShoePosition: () => {
    const { layout } = get();
    if (!layout) return undefined;
    const endpoint = LayoutQueries.getEndpoint(layout, BlackjackEndpointIds.shoe);
    return endpoint?.position;
  },

  getDealerPosition: () => {
    const { layout } = get();
    if (!layout) return undefined;
    const endpoint = LayoutQueries.getEndpoint(layout, BlackjackEndpointIds.dealer);
    return endpoint?.position;
  },
}));

// ===========================================
// SELECTORS
// ===========================================

export const selectLayout = (state: LayoutState) => state.layout;
export const selectLayoutVersion = (state: LayoutState) => state.version;
export const selectIsEditing = (state: LayoutState) => state.isEditing;
export const selectHasLayout = (state: LayoutState) => state.layout !== null;

// ===========================================
// NON-REACTIVE ACCESSORS
// ===========================================
// For use in animation utilities that need positions
// but shouldn't trigger React re-renders

/**
 * Get the current layout without subscribing to changes.
 * Use this in animation utilities (chipAnimations.ts, cardAnimations.ts).
 */
export function getLayout(): TableLayout | null {
  return useLayoutStore.getState().layout;
}

/**
 * Get a specific endpoint position without subscribing.
 * Returns undefined if layout not loaded or endpoint not found.
 */
export function getEndpointPosition(endpointId: string): Point2D | undefined {
  const layout = getLayout();
  if (!layout) return undefined;
  return LayoutQueries.getGlobalPosition(layout, endpointId);
}

/**
 * Get kitty position for a visual seat position.
 * For use in chip animations.
 */
export function getKittyPosition(visualSeatPos: number): Point2D | undefined {
  return getEndpointPosition(BlackjackEndpointIds.kitty(visualSeatPos));
}

/**
 * Get tray chip position for a denomination.
 * For use in payout animations (dealer -> kitty).
 */
export function getTrayChipPosition(denomination: number): Point2D | undefined {
  return getEndpointPosition(BlackjackEndpointIds.trayChip(denomination));
}

/**
 * Get rail chip position for a denomination.
 * For use in win animations (kitty -> rail).
 */
export function getRailChipPosition(denomination: number): Point2D | undefined {
  return getEndpointPosition(BlackjackEndpointIds.railChip(denomination));
}

/**
 * Get staged bet position.
 * For use in bet placement animations.
 */
export function getStagedBetPosition(): Point2D | undefined {
  return getEndpointPosition(BlackjackEndpointIds.stagedBet);
}

/**
 * Get all chip positions for animations.
 * Returns a convenient object with all positions needed for chip animations.
 */
export function getChipAnimationPositions(visualSeatPos: number): {
  kitty: Point2D | undefined;
  stagedBet: Point2D | undefined;
  trayChips: Map<number, Point2D>;
  railChips: Map<number, Point2D>;
} {
  const trayChips = new Map<number, Point2D>();
  const railChips = new Map<number, Point2D>();

  for (const denom of BLACKJACK_DENOMINATIONS) {
    const trayPos = getTrayChipPosition(denom);
    if (trayPos) trayChips.set(denom, trayPos);

    const railPos = getRailChipPosition(denom);
    if (railPos) railChips.set(denom, railPos);
  }

  return {
    kitty: getKittyPosition(visualSeatPos),
    stagedBet: getStagedBetPosition(),
    trayChips,
    railChips,
  };
}

// ===========================================
// HOOKS
// ===========================================

/**
 * Hook for components that need layout data.
 * Subscribes to layout changes and triggers re-render.
 */
export function useLayout() {
  const layout = useLayoutStore((state) => state.layout);
  const version = useLayoutStore((state) => state.version);
  return { layout, version, hasLayout: layout !== null };
}

/**
 * Hook for DEV CONTROLS components.
 * Provides mutation methods for editing layout.
 */
export function useLayoutEditor() {
  const store = useLayoutStore();
  return {
    layout: store.layout,
    isEditing: store.isEditing,
    setEditing: store.setEditing,
    updatePosition: store.updatePosition,
    updateScale: store.updateScale,
    batchUpdate: store.batchUpdate,
    initializeDefault: store.initializeDefault,
  };
}

/**
 * Hook for getting a specific endpoint.
 * Re-renders when that endpoint changes.
 */
export function useEndpoint(endpointId: string) {
  return useLayoutStore((state) => {
    if (!state.layout) return undefined;
    return LayoutQueries.getEndpoint(state.layout, endpointId);
  });
}

/**
 * Hook for getting kitty position for a seat.
 * Re-renders when that kitty's position changes.
 */
export function useKittyPosition(visualSeatPos: number) {
  return useLayoutStore((state) => {
    if (!state.layout) return undefined;
    return LayoutQueries.getKitty(state.layout, visualSeatPos)?.position;
  });
}

export default useLayoutStore;
